import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useAppStore } from '../app/store/useAppStore';
import { SignaturePad } from '../features/signature/SignaturePad';
import { archiveRepository } from '../db/gcam-db';
import { composeResultImage, shareOrDownload } from '../features/export/composeResult';
import { Icon } from '../components/ui/Icon';
import { Modal } from '../components/ui/Modal';
import type { ResultViewProps } from '../modules/capture/types';
import { useObjectUrl } from '../features/media/useObjectUrl';
import '../styles/result-polish.css';

interface FrameSize { width: number; height: number }

function useContainedFrame(aspectRatio: number | null) {
  const containerRef = useRef<HTMLElement>(null);
  const [frameSize, setFrameSize] = useState<FrameSize | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !aspectRatio) {
      setFrameSize(null);
      return;
    }

    const update = () => {
      const { width, height } = container.getBoundingClientRect();
      if (!width || !height) return;
      const containerRatio = width / height;
      const next = containerRatio > aspectRatio
        ? { width: height * aspectRatio, height }
        : { width, height: width / aspectRatio };
      setFrameSize((current) => current
        && Math.abs(current.width - next.width) < 0.5
        && Math.abs(current.height - next.height) < 0.5
        ? current
        : next);
    };

    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(container);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [aspectRatio]);

  return { containerRef, frameSize };
}

export function ResultPage({ encounter }: ResultViewProps) {
  const navigate = useAppStore((state) => state.navigate);
  const setEncounter = useAppStore((state) => state.setEncounter);
  const setNotice = useAppStore((state) => state.setNotice);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [photoMeasurement, setPhotoMeasurement] = useState<{ url: string; aspectRatio: number } | null>(null);
  const photoUrl = useObjectUrl(encounter.photoBlob);
  const photoAspectRatio = photoMeasurement?.url === photoUrl ? photoMeasurement.aspectRatio : null;
  const { containerRef: photoStageRef, frameSize } = useContainedFrame(photoAspectRatio);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  const created = new Date(encounter.createdAt);
  const signature = encounter.signature?.type === 'svg-path' && typeof encounter.signature.data === 'string' ? encounter.signature.data : '';
  const peopleLabel = encounter.people.map((person) => person.label).join(' × ') || 'GUEST';
  const photoFrameStyle = frameSize ? ({ width: `${frameSize.width}px`, height: `${frameSize.height}px` } satisfies CSSProperties) : undefined;

  const updateSignature = (data: string) => setEncounter({
    ...encounter,
    signature: data ? { type: 'svg-path', data } : undefined,
    resultImageBlob: undefined,
  });

  const saveArchive = async () => {
    setSaving(true);
    try {
      const resultImageBlob = await composeResultImage(encounter);
      const record = { ...encounter, resultImageBlob };
      await archiveRepository.save(record);
      setEncounter(record);
      setNotice({ message: '已保存至此裝置的 G-Camera 相簿', actionLabel: '查看', onAction: () => navigate('ARCHIVE') });
    } catch { setNotice('無法保存至本機相簿；仍可使用「保存照片」。'); }
    finally { setSaving(false); }
  };

  const savePhoto = async () => {
    setSaving(true);
    try {
      const blob = encounter.resultImageBlob ?? await composeResultImage(encounter);
      const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIos && !navigator.canShare?.({ files: [new File([blob], 'g-camera.png', { type: 'image/png' })] })) {
        setPreviewUrl(URL.createObjectURL(blob));
        setNotice('請長按預覽圖片保存至相簿');
      } else {
        const outcome = await shareOrDownload(blob, `${encounter.id}.png`);
        setNotice(outcome === 'shared' ? '已開啟系統分享' : '作品圖片已下載');
      }
    } catch (error) { if (error instanceof DOMException && error.name === 'AbortError') return; setNotice('圖片輸出失敗，請再試一次。'); }
    finally { setSaving(false); }
  };

  return <main className="screen result-screen result-enter">
    <header className="result-header"><button className="toolbar-button" onClick={() => navigate('HOME')}><Icon name="back"/>首頁</button><span>相遇結果</span><b className="focus-mark">[•]</b></header>
    <section className="result-id"><span className="micro-label">ID</span><strong>{encounter.id}</strong></section>
    <figure ref={photoStageRef} className={`result-photo ${photoUrl ? '' : 'is-loading'}`}>
      {photoUrl ? <div className="result-photo-frame" style={photoFrameStyle}>
        <img
          src={photoUrl}
          alt="本次相遇拍攝結果"
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget;
            if (photoUrl && naturalWidth && naturalHeight) setPhotoMeasurement({ url: photoUrl, aspectRatio: naturalWidth / naturalHeight });
          }}
        />
        <div className="people-line" title={peopleLabel}>{peopleLabel}</div>
        <button className="result-download" type="button" aria-label="保存照片" disabled={saving} onClick={() => void savePhoto()}><Icon name="download" size={20}/></button>
        <i className="corner tl"/><i className="corner tr"/><i className="corner bl"/><i className="corner br"/>
      </div> : <span className="spinner" aria-label="正在載入拍攝結果"/>}
    </figure>
    <section className="result-metadata result-metadata-compact mono">
      <div className="result-time"><span>{created.toLocaleDateString('zh-TW')}</span><span>{created.toLocaleTimeString('zh-TW', { hour12: false })}</span></div>
      <div className="result-gps">{encounter.location.latitude == null ? 'GPS 未取得' : `${encounter.location.latitude.toFixed(4)}°, ${encounter.location.longitude?.toFixed(4)}°`}</div>
      <div className="result-palette"><div>{encounter.palette.slice(0, 5).map((sample) => <i key={sample.hex} style={{ background: sample.hex }}/>)}</div><strong>{encounter.paletteName}</strong></div>
    </section>
    <section className="signature-section signature-section-compact"><div className="section-heading"><span>SIGNATURE / 簽名</span><small>MULTI-STROKE · ONE-OFF</small></div><SignaturePad value={signature} onChange={updateSignature} disabled={saving}/></section>
    <div className="result-actions"><button className="outline-button" disabled={saving} onClick={() => { setEncounter(null); navigate('CAMERA_INIT'); }}><Icon name="switch"/>重拍</button><button className="outline-button accent-border" aria-busy={saving} disabled={saving} onClick={() => void saveArchive()}><Icon name="archive"/>{saving ? '保存中' : '保存至相簿'}</button></div>
    {previewUrl ? <Modal title="保存照片" kicker="IOS PREVIEW" onClose={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}><p className="privacy-note">請長按下方圖片，選擇「儲存到照片」。</p><img className="ios-preview" src={previewUrl} alt="可長按保存的結果作品"/></Modal> : null}
  </main>;
}
